import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { checkValuePropStatus } from 'redux/slices/discoverySlice';
import { updateUiState } from 'redux/slices/uiSlice';
import { hideAlert, showAlert } from 'redux/slices/alertSlice';
import { startPolling } from 'utils/pollingInterval';

/**
 * Polls an in-flight Ask-AI ("expert bot") conversation request from the
 * Dashboard layout, so that a user who navigates away from the AI pages still
 * gets told when their answer is ready.
 *
 * Deliberately inert on `/ask-ai`, `/digital-twin` and `/conversation-ai`:
 * those pages run their own polling, and two pollers on one request would
 * double-dispatch the completion.
 *
 * Split out of `Dashboard.js` in Phase 6; called unconditionally from a
 * component that never unmounts, so the 1s interval and its `isMounted` guard
 * keep exactly the lifetime they had inline.
 */
const useAsyncExpertBotPolling = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const asyncExpertBotConversation = useSelector(
    (state) => state.uiStates.asyncExpertBotConversation,
  );
  const asyncExpertBotConversationTimerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    if (
      asyncExpertBotConversation?.requestId &&
      location.pathname !== '/ask-ai' &&
      location.pathname !== '/digital-twin' &&
      location.pathname !== '/conversation-ai'
    ) {
      if (asyncExpertBotConversationTimerRef.current) {
        clearInterval(asyncExpertBotConversationTimerRef.current);
      }
      asyncExpertBotConversationTimerRef.current = startPolling(() => {
        dispatch(checkValuePropStatus(asyncExpertBotConversation?.requestId))
          .unwrap()
          .then((data) => {
            if (!isMounted) return;
            if (data?.status === 'completed' || data?.status === 'failed') {
              clearInterval(asyncExpertBotConversationTimerRef.current);
              if (data?.status === 'completed') {
                dispatch(
                  updateUiState({
                    key: 'asyncExpertBotConversation',
                    value: {
                      requestId: null,
                      conversationId:
                        asyncExpertBotConversation?.conversationId,
                      conversationData:
                        asyncExpertBotConversation?.conversationData,
                      completedResponse: data,
                    },
                  }),
                );
                dispatch(
                  showAlert({
                    message: 'Your AI response is ready',
                    showCross: true,
                    title: null,
                    type: 'success',
                    autoHideDuration: 5000,
                    vertical: 'top',
                    horizontal: 'center',
                    onClick: () => {
                      dispatch(hideAlert());
                      history.push(
                        `/ask-ai?conId=${asyncExpertBotConversation?.conversationId}`,
                      );
                    },
                  }),
                );
              } else {
                dispatch(
                  updateUiState({
                    key: 'asyncExpertBotConversation',
                    value: null,
                  }),
                );
              }
            }
          })
          .catch(() => {});
      }, 1000);
    } else {
      if (asyncExpertBotConversationTimerRef.current) {
        clearInterval(asyncExpertBotConversationTimerRef.current);
      }
    }
    return () => {
      isMounted = false;
      if (asyncExpertBotConversationTimerRef.current) {
        clearInterval(asyncExpertBotConversationTimerRef.current);
      }
    };
  }, [asyncExpertBotConversation?.requestId, location.pathname]);
};

export default useAsyncExpertBotPolling;
